/**
 * RewriteExperienceOutput — renders the rewrite_experience step: each position
 * with its before/after bullet rewrites and per-bullet rationale.
 */

import { Box, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useTranslation } from "react-i18next";
import type { ResumeStepData } from "../../../api/client";
import LessonMarkdown from "../../LessonMarkdown";

export default function RewriteExperienceOutput({ step }: { step: ResumeStepData }) {
  const theme = useTheme();
  const { t } = useTranslation("resume");
  const o = step.output ?? {};
  const positions: any[] = o.positions ?? [];

  return (
    <Stack spacing={2.5}>
      {positions.map((p, pi) => {
        const bullets: any[] = p?.bullets ?? [];
        return (
          <Box key={`pos-${pi}`}>
            <Typography variant="subtitle1" fontWeight={500}>
              {p?.title ?? t("RewriteExperienceOutput.positionFallback")}
              {p?.company ? (
                <Typography component="span" variant="body2" color="text.secondary" fontWeight={500}>
                  {t("RewriteExperienceOutput.companySuffix", { company: p.company })}
                </Typography>
              ) : null}
            </Typography>

            <Stack spacing={1.5} mt={1}>
              {bullets.map((b, bi) => (
                <Box
                  key={`bullet-${pi}-${bi}`}
                  sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.primary.light, 0.04) }}
                >
                  {b?.original && (
                    <Typography
                      variant="body2"
                      sx={{ color: "text.disabled", textDecoration: "line-through" }}
                    >
                      {b.original}
                    </Typography>
                  )}
                  {b?.rewritten && (
                    <Stack direction="row" spacing={1} alignItems="flex-start" mt={b?.original ? 0.75 : 0}>
                      <ArrowForwardIcon sx={{ fontSize: 16, color: theme.palette.primary.main, mt: "3px" }} />
                      <Typography variant="body2" fontWeight={500} color="text.primary">
                        {b.rewritten}
                      </Typography>
                    </Stack>
                  )}
                  {b?.rationale && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                      {b.rationale}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        );
      })}

      <LessonMarkdown>{step.narrative_markdown ?? ""}</LessonMarkdown>
    </Stack>
  );
}
